import React, { useRef, useState } from 'react';
import {
  Check,
  CircleAlert,
  Copy,
  FileSpreadsheet,
  FileText,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { Alert, AlertAction, AlertTitle } from '@dashboard/components/ui/alert';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import { toast } from '@dashboard/context/ToastContext';
import getPageTypeLabel from '@dashboard/services/getPageTypeLabel';
import StepList from './StepList';
import ChangeList from './ChangeList';
import PointCard from './PointCard';
import UploadCard from './UploadCard';

// Only an image has a public url: a document sits in the private files bucket.
function SentAttachment({ attachment }) {
  if (attachment.url) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer">
        <img
          src={attachment.url}
          alt={attachment.name}
          className="size-16 rounded-lg border object-cover"
        />
      </a>
    );
  }
  const Icon = attachment.type === 'text/csv' ? FileSpreadsheet : FileText;
  return (
    <span className="flex h-16 max-w-48 items-center gap-1.5 rounded-lg border px-3 text-xs">
      <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate">{attachment.name}</span>
    </span>
  );
}

function UserBubble({ turn }) {
  return (
    <div className="flex flex-col items-end gap-1">
      {turn.mode === 'ask' && <Badge variant="secondary">Ask</Badge>}
      {turn.attachments?.length > 0 && (
        <ul aria-label="Attached files" className="flex max-w-[85%] flex-wrap justify-end gap-1.5">
          {turn.attachments.map(attachment => (
            <li key={attachment.fileId}>
              <SentAttachment attachment={attachment} />
            </li>
          ))}
        </ul>
      )}
      {turn.content && (
        <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-muted px-3.5 py-2.5 text-sm">
          {turn.content}
        </p>
      )}
    </div>
  );
}

function ConversationDivider() {
  return (
    <div className="flex items-center gap-3 text-xs leading-[17px] text-muted-foreground">
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
      New conversation
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </div>
  );
}

const COPIED_MS = 2000;

function CopyReplyButton({ text }) {
  const [isCopied, setIsCopied] = useState(false);
  const resetTimer = useRef(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      toast({ title: "Couldn't copy the reply. Try again.", type: 'error' });
      return;
    }
    setIsCopied(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setIsCopied(false), COPIED_MS);
  };

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label="Copy reply"
      className="text-muted-foreground"
      onClick={copy}
    >
      {isCopied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
    </Button>
  );
}

function TurnError({ error, canAct, onRetry, onNewChat }) {
  if (error.kind === 'limit') {
    return (
      <Alert>
        <TriangleAlert aria-hidden="true" />
        <AlertTitle className="line-clamp-none">{error.message}</AlertTitle>
      </Alert>
    );
  }

  const isMismatch = error.kind === 'mismatch';
  return (
    <Alert variant="destructive">
      <CircleAlert aria-hidden="true" />
      <AlertTitle className="line-clamp-none">{error.message}</AlertTitle>
      {canAct && (
        <AlertAction>
          <Button variant="outline" size="sm" onClick={isMismatch ? onNewChat : onRetry}>
            {isMismatch ? 'New chat' : 'Try again'}
          </Button>
        </AlertAction>
      )}
    </Alert>
  );
}

export default function AITurn({
  turn,
  pageType,
  isLatest,
  onFixPoint,
  onShowChange,
  onShowPoint,
  onOpenLocation,
  onRetry,
  onNewChat,
  guide,
}) {
  if (turn.role === 'user') return <UserBubble turn={turn} />;
  if (turn.role === 'divider') return <ConversationDivider />;
  // The guide itself sits in the message box; one opened from a section says what it is for.
  if (turn.role === 'guide') {
    if (!guide || turn.from !== 'section') return null;
    return (
      <p className="text-[13px] leading-[19px] text-muted-foreground">
        {`Ask about ${guide.section.label}, or describe a change.`}
      </p>
    );
  }

  // A turn restored from history has no outline on the preview to show.
  const canShow = isLatest && !turn.restored;
  // A point already offers that uploader, so the turn shows it once.
  const pointUploads = new Set(turn.points.map(point => point.upload).filter(Boolean));
  const offeredUploads = (turn.uploads ?? []).filter(upload => !pointUploads.has(upload.kind));

  return (
    <div className="flex items-start gap-2">
      <span aria-hidden="true" className="flex size-5.5 shrink-0 items-center justify-center">
        <Sparkles className="size-3.5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-3 pt-px">
        <StepList
          steps={turn.steps}
          running={turn.status === 'streaming'}
          // An Ask turn's writes are refused by design, and an errored turn's may still have landed.
          keepFailedWrites={turn.status === 'done' && turn.mode !== 'ask'}
          pageType={pageType}
        />
        {turn.text && (
          <p className="whitespace-pre-wrap break-words text-sm leading-[21px] text-foreground">
            {turn.text}
          </p>
        )}
        <ChangeList
          changes={turn.changes}
          pageType={pageType}
          onShow={canShow ? onShowChange : undefined}
          onOpen={onOpenLocation}
        />
        {turn.points.length > 0 && (
          <ol className="flex flex-col gap-2">
            {turn.points.map((point, index) => (
              <PointCard
                key={`${point.n}:${index}`}
                point={point}
                pageType={pageType}
                onShow={canShow ? onShowPoint : undefined}
                onFix={onFixPoint}
                onOpen={onOpenLocation}
              />
            ))}
          </ol>
        )}
        {/* Still usable on a restored turn: the uploader adds to the page as it is now. */}
        {offeredUploads.map((upload, index) => (
          <UploadCard
            key={`${upload.kind}:${index}`}
            upload={upload}
            pageType={pageType}
            defaultOpen={canShow}
            onOpen={onOpenLocation}
          />
        ))}
        {turn.status === 'stopped' && (
          <p className="text-xs leading-[17px] text-muted-foreground">
            Stopped. Changes already saved stay on your {getPageTypeLabel({ type: pageType })}.
          </p>
        )}
        {turn.status === 'error' && turn.error && (
          // retry() resends the newest message, so only the newest turn offers it.
          <TurnError error={turn.error} canAct={isLatest} onRetry={onRetry} onNewChat={onNewChat} />
        )}
        {turn.status !== 'streaming' && turn.text && (
          <div className="-mt-1.5 flex">
            <CopyReplyButton text={turn.text} />
          </div>
        )}
      </div>
    </div>
  );
}
